import React, { useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import html2canvas from 'html2canvas';
import { FiPhone, FiMessageSquare, FiGlobe, FiShare2, FiDownload, FiMail, FiMapPin } from 'react-icons/fi';

const DigitalCardPage = () => {
  const { user, business } = useAuth();
  const { showToast } = useToast();
  const cardRef = useRef(null);

  if (!business) return null;

  const handleCall = () => {
    window.location.href = `tel:${business.phone}`;
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`Hi ${business.businessName}, I got your digital visiting card.`);
    window.open(`https://wa.me/${business.whatsapp}?text=${text}`, '_blank');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${business.businessName} - Digital Visiting Card`,
        text: `Check out ${business.businessName}'s digital card!`,
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast('Digital Card link copied to clipboard!', 'info');
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      showToast('Generating digital card image...', 'info');
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `${business.slug}-digital-card.png`;
      link.click();
      showToast('Digital Card downloaded successfully!', 'success');
    } catch (err) {
      showToast('Failed to download digital card', 'error');
    }
  };

  return (
    <div style={{ maxWidth: '650px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
          Digital Visiting Card
        </h1>
        <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
          Share your professional business card digitally on WhatsApp or print as PNG.
        </p>
      </div>

      {/* VISITING CARD PREVIEW CONTAINER */}
      <div
        ref={cardRef}
        style={{
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          borderRadius: '24px',
          border: '2px solid rgba(99, 102, 241, 0.4)',
          boxShadow: 'var(--shadow-lg), var(--shadow-glow)',
          padding: '2.5rem 2rem',
          color: '#ffffff',
          marginBottom: '2rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1.5rem' }}>
          {business.logo ? (
            <img
              src={business.logo}
              alt={business.businessName}
              style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary-500)' }}
            />
          ) : (
            <div
              style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--primary-500), var(--secondary-500))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.8rem',
                fontWeight: '800',
                color: '#fff'
              }}
            >
              {business.businessName.charAt(0).toUpperCase()}
            </div>
          )}

          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: '800' }}>
              {business.businessName}
            </h2>
            <p style={{ color: 'var(--secondary-500)', fontSize: '0.9rem', fontWeight: '600' }}>
              {user?.name || 'Business Owner'} • {business.category || 'Business Owner'}
            </p>
          </div>
        </div>

        {business.description && (
          <p style={{ color: '#CBD5E1', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            {business.description}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem', fontSize: '0.9rem', color: '#E2E8F0' }}>
          {business.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FiPhone style={{ color: 'var(--primary-500)' }} /> <span>📞 {business.phone}</span>
            </div>
          )}
          {business.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FiMail style={{ color: 'var(--primary-500)' }} /> <span>✉ {business.email}</span>
            </div>
          )}
          {(business.city || business.address) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FiMapPin style={{ color: 'var(--primary-500)' }} /> <span>📍 {business.address} {business.city}</span>
            </div>
          )}
        </div>

        {/* Action Buttons inside Card */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {business.phone && (
            <button onClick={handleCall} className="action-btn-call" style={{ flex: 1 }}>
              <FiPhone /> Call
            </button>
          )}
          {business.whatsapp && (
            <button onClick={handleWhatsApp} className="action-btn-whatsapp" style={{ flex: 1 }}>
              <FiMessageSquare /> WhatsApp
            </button>
          )}
          {business.website && (
            <a href={business.website} target="_blank" rel="noreferrer" className="action-btn-share" style={{ flex: 1 }}>
              <FiGlobe /> Website
            </a>
          )}
        </div>
      </div>

      {/* Action Controls */}
      <div style={{ display: 'flex', gap: '1.25rem', justifyContent: 'center' }}>
        <button onClick={handleShare} className="btn btn-secondary btn-lg">
          <FiShare2 /> Share Card
        </button>
        <button onClick={handleDownload} className="btn btn-primary btn-lg">
          <FiDownload /> Download Card (PNG)
        </button>
      </div>
    </div>
  );
};

export default DigitalCardPage;
